import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import styles from './OrderReturn.module.scss';
import { iRootDispatch, iRootState, select } from '../../../../store/store';
import DataSource, { DataSourceOptions } from 'devextreme/data/data_source';
import ProgressBarBoxes, { RemainingTrailerSpace } from '../../progress-bar-boxes/ProgressBarBoxes';
import Button from 'fulogics-ui-web/dist/layout/dx/Button/Button';
import DataGrid from 'fulogics-ui-web/dist/layout/dx/DataGrid/DataGrid';
import {
    orderStepsReturnAddFirstLoadPlaceId,
    orderStepsReturnAddNewLoadPlaceId,
    orderStepsReturnGridId,
} from './inspectorOptions';
import withInspector from 'fulogics-ui-web/dist/layout/inspector/hoc/withInspector';
import { OrderReturnStepData, OrderReturnStepDataKeys } from '../OrderReturnStepData';
import OrderReturnStepForm, {
    OrderReturnStepFormData,
    OrderReturnStepPayloadItemData,
} from '../order-return-step-form/OrderReturnStepForm';
import {
    COSOrderReturnStepStore,
    insertOrderReturnStepToCollectionPayload,
} from '../../../../store/models/COSOrderReturnStepModel';
import { useMemoDataSource } from 'fulogics-ui-web/dist/layout/hooks/useMemoDataSource';
import enOrderStepType from '../../../../api/enums/enOrderStepType';
import { useStoreSubscription } from 'fulogics-ui-core/dist/layout/hooks/useStoreSubscription';
import { nameof } from 'ts-simple-nameof';
import { deleteItemPayload } from '../../../../store/models/COSOrderReturnStepPayloadModel';
import { Template } from 'devextreme-react/core/template';
import ForwardStepTypeColumnTemplate from '../../../templates/dataGrid/column/forward-step-type/ForwardStepTypeColumnTemplate';
import { custom } from 'devextreme/ui/dialog';
import i18n from '../../../../translations/i18n';
import enArrowDirection from '../../../../api/enums/enArrowDirection';
import { enTMSItemType, IdType } from 'fulogics-ui-core';
import { COSOrderForwardStepStore } from '../../../../store/models/COSOrderForwardStepModel';
import ValidationInfoColumnTemplate from '../../../templates/dataGrid/column/validation-info-column-template/ValidationInfoColumnTemplate';
import OrderReturnPlannedStartDateColumnTemplate from '../../../templates/dataGrid/column/order-return-planned-start-date-column-template/OrderReturnPlannedStartDateColumnTemplate';
import { OrderValidationResult } from '../../order-error-info/OrderErrorInfo';
import Validator, { AsyncRule, RangeRule, RequiredRule } from 'devextreme-react/validator';
import { orderEditValidationGroup } from '../inspectorOptions';
import { getRemainingReturnTrailerSpacePayload } from '../../../../store/models/COSOrdersModel';
import { StyledWarningIcon } from './styled';
import { getTrailerCapacity } from '../../progress-bar-boxes/getTrailerCapacity';

const selection = select((_models: any) => ({}));

const mapStateToProps = (state: iRootState) =>
    ({
        createReturnStepsCollectionDataSource: (returnStepsCollectionId?: string) => {
            let opts = COSOrderReturnStepStore.createByCollectionId(returnStepsCollectionId, {
                defaultOrderByPolicy: 'timetable',
            });
            //
            if (opts) opts.select = OrderReturnStepItemKeys;
            //
            return opts;
        },
        createForwardStepsCollectionDataSource: (forwardStepsCollectionId?: string) => {
            const opts = COSOrderForwardStepStore.createByCollectionId(forwardStepsCollectionId);
            if (opts) opts.select = ['id', 'plannedStartDate'];

            return opts;
        },
        //
        ...(selection(state) as any),
    } as OrderReturnDisplayOptions);

const mapDispatch = (_dispatch: iRootDispatch, _ownDispatch: OrderReturnDisplayOptions) =>
    ({
        insertOrderReturnStep: (payload?: insertOrderReturnStepToCollectionPayload) =>
            // @ts-ignore
            _dispatch?.COSOrderReturnStep?.insertToCollection<OrderReturnStepItem>({
                responseDataKeys: OrderReturnStepItemKeys,
                //
                ...payload,
            }),
        //
        removeStep: _dispatch?.COSOrderReturnStep?.deleteItem,
        getRemainingTrailerSpace: _dispatch?.COSOrders?.getRemainingReturnTrailerSpace,
        //
        ..._ownDispatch,
    } as OrderReturnDisplayOptions);

// TODO
// w gridzie powinna zostać użyta kolumna "RouteLocationMultiRowColumn" jako "lokalizacja"
// komunikacja z dodawaniem nowych palet do załadunku
//  isReadOnly

const OrderReturn = connect(
    mapStateToProps,
    mapDispatch as any
)((props: OrderReturnProps) => {
    const { t } = useTranslation('OrderReturn');
    const [carrierStackEnabled, setCarrierStackEnabled] = useState<boolean>(false);
    const [focusedStep, setFocusedStep] = useState<OrderReturnStepData | undefined>(undefined);
    const [lastForwardStepStartDate, setLastForwardStepStartDate] =
        useState<Date | undefined>(undefined);
    const [remainingTrailerSpace, setRemainingTrailerSpace] =
        useState<RemainingTrailerSpace | undefined>();

    const orderStepsReturnDataSource = useMemoDataSource(
        () => props.createReturnStepsCollectionDataSource?.(props.data?.returnSteps?.id),
        [props.data?.returnSteps?.id],
        true
    );

    const orderStepsForwardDataSource = useMemoDataSource(
        () => props.createForwardStepsCollectionDataSource?.(props.data?.forwardSteps?.id),
        [props.data?.forwardSteps?.id]
    );
    const [orderStepsReturnDataSourceTotalCount, setOrderStepsReturnDataSourceTotalCount] =
        useState<number>(0);

    const dataSourcePushCallback = useCallback(
        async (changes?: { type: string }[]) => {
            if (!changes || changes?.find((q) => q.type === 'insert')) {
                orderStepsReturnDataSource?.load().then((q) => {
                    setOrderStepsReturnDataSourceTotalCount(
                        orderStepsReturnDataSource?.items()?.length ?? 0
                    );
                });
            }
        },
        [orderStepsReturnDataSource]
    );
    useStoreSubscription('push', dataSourcePushCallback, orderStepsReturnDataSource?.store());

    useEffect(() => {
        dataSourcePushCallback(undefined);
    }, [orderStepsReturnDataSource]);

    // Dodawanie nowego pustego miejsca rozładunku
    const onLoadPlaceAdd = useCallback(async () => {
        if (props.insertOrderReturnStep !== undefined) {
            let r = await props.insertOrderReturnStep({
                collectionId: props.data?.returnSteps?.id,
            });
            //
            setFocusedStep(r);
        }
    }, [props.insertOrderReturnStep, orderStepsReturnDataSource]);

    // Ustawianie wybranego miejsca rozładunku
    const onOrderStepsReturnGridFocusedRowChanged = useCallback((e: any) => {
        const dataRow = e.row && e.row.data;

        if (!!dataRow) {
            setFocusedStep(dataRow);
        }
    }, []);

    const onRemoveStepDialog = (args: deleteItemPayload) =>
        custom({
            messageHtml: `<div <div class="confirmCustomDialogContainer">
        <div class="confirmCustomDialogContainerTop">${t(
            'removePlace'
        )}</div><div class="confirmCustomDialogContainerBottom">${t(
                'confirmRemovePlace'
            )}</div></div>`,
            showTitle: false,
            buttons: [
                {
                    // text: "t~OrderNewScreenDonePopup:confirmOrder",
                    text: i18n.t('OrderReturn:cancel'),
                    type: 'danger',
                    stylingMode: 'contained',
                },
                {
                    stylingMode: 'contained',
                    type: 'success',
                    // text: "t~OrderNewScreenDonePopup:cancel",
                    text: i18n.t('OrderReturn:confirm'),
                    onClick: (e) => {
                        props.removeStep?.(args);
                        //
                        setFocusedStep(undefined);
                    },
                },
            ],
        });

    const removeStep = useCallback(
        async (args: deleteItemPayload) => {
            onRemoveStepDialog(args).show();
            await orderStepsReturnDataSource?.reload();
        },
        [props.removeStep, orderStepsReturnDataSource]
    );

    const onReturnStepFormFieldDataChanged = useCallback(
        (dataField?: string, value?: any) => {
            if (dataField === nameof<OrderReturnStepFormData>((q) => q.payload)) {
                orderStepsReturnDataSource?.reload();
            }
        },
        [orderStepsReturnDataSource]
    );

    const calculateRemainingSpace = useCallback(() => {
        if (props.getRemainingTrailerSpace && props.data?.id) {
            return props
                .getRemainingTrailerSpace({
                    id: props.data.id,
                })
                .then(setRemainingTrailerSpace);
        }

        return Promise.resolve();
    }, [props.getRemainingTrailerSpace]);

    useEffect(() => {
        calculateRemainingSpace();
    }, [calculateRemainingSpace]);

    useEffect(() => {
        const handleDataChange = () => {
            props?.validateOrder?.();
            calculateRemainingSpace();
        };
        orderStepsReturnDataSource?.on('changed', handleDataChange);
        return () => {
            orderStepsReturnDataSource?.off('changed', handleDataChange);
        };
    }, [orderStepsReturnDataSource, calculateRemainingSpace, props?.validateOrder]);

    const { filledTrailerCapacity, maxTrailerCapacity } = getTrailerCapacity(remainingTrailerSpace);

    const maxCapacityReached =
        filledTrailerCapacity > maxTrailerCapacity && props.data?.returnSteps;

    useEffect(() => {
        return () => {
            orderStepsReturnDataSource?.items().forEach(async (e) => {
                if (e.payload.carrierCount === 0 && e.type == enTMSItemType.Load) {
                    await orderStepsReturnDataSource?.store().remove(e.id);
                }
            });
        };
    }, []);

    useEffect(() => {
        if (orderStepsForwardDataSource) {
            orderStepsForwardDataSource
                .load()
                .then((steps) => {
                    const stepsByDate = steps
                        ?.filter((step: any) => !!step?.plannedStartDate)
                        .sort(
                            (a: any, b: any) =>
                                new Date(b?.plannedStartDate).getTime() -
                                new Date(a?.plannedStartDate).getTime()
                        );
                    setLastForwardStepStartDate(stepsByDate?.[0]?.plannedStartDate);
                })
                .catch(() => {
                    setLastForwardStepStartDate(undefined);
                });
        }
    }, [orderStepsForwardDataSource]);

    const validateWithBackend = async (params: { value?: { id?: IdType }[] }) => {
        try {
            const validationResult = await props.validateOrder?.();

            const brokenRules = validationResult?.brokenRules?.filter(({ relatedEntityId }) =>
                params?.value?.some(({ id }) => id === relatedEntityId)
            );

            return !brokenRules?.length;
        } catch (e) {
            return false;
        }
    };

    const validationAdapter = useMemo(
        () => ({
            getValue: () => orderStepsReturnDataSource?.items(),
        }),
        [orderStepsReturnDataSource]
    );

    const capacityValidationAdapter = useMemo(
        () => ({
            getValue: () => filledTrailerCapacity,
        }),
        [filledTrailerCapacity]
    );

    const handlePayloadChange = (items?: OrderReturnStepPayloadItemData[]) => {
        setCarrierStackEnabled(
            items?.some((item) => item?.maximumAllowedStacks && item?.maximumAllowedStacks > 1) ||
                false
        );
    };

    return (
        <div className={classNames(styles.container, props.className)}>
            <div className={classNames(styles.left)}>
                {focusedStep && (
                    <OrderReturnStepForm
                        data={focusedStep}
                        isReadOnly={focusedStep.type === enOrderStepType.Unload}
                        removeStep={removeStep}
                        onFieldDataChanged={onReturnStepFormFieldDataChanged}
                        lastForwardStepStartDate={lastForwardStepStartDate}
                        onPayloadChanged={handlePayloadChange}
                    />
                )}
            </div>
            <div className={classNames(styles.right)}>
                <div id="rightGrids" className={classNames(styles.topRight)}>
                    {orderStepsReturnDataSourceTotalCount > 0 ? (
                        <>
                            <div>{t('dataGridTitle')}</div>
                            <DataGrid
                                inspectorId={orderStepsReturnGridId}
                                dataSource={orderStepsReturnDataSource}
                                focusedRowEnabled
                                focusedRowKey={focusedStep?.id}
                                repaintChangesOnly
                                onFocusedRowChanged={onOrderStepsReturnGridFocusedRowChanged}
                            >
                                <Template
                                    name="ReturnStepType"
                                    render={ForwardStepTypeColumnTemplate}
                                />
                                <Template
                                    name="OrderReturnPlannedStartDateColumn"
                                    render={OrderReturnPlannedStartDateColumnTemplate}
                                />
                                <Template
                                    name="ValidationInfoColumn"
                                    render={ValidationInfoColumnTemplate}
                                />
                            </DataGrid>
                            <Validator
                                validationGroup={orderEditValidationGroup}
                                adapter={validationAdapter}
                            >
                                <AsyncRule validationCallback={validateWithBackend} />
                            </Validator>
                            <Validator
                                validationGroup={orderEditValidationGroup}
                                adapter={capacityValidationAdapter}
                            >
                                <RangeRule max={maxTrailerCapacity} />
                                <RequiredRule />
                            </Validator>
                            <div className={classNames(styles.addNextPlace)}>
                                <Button
                                    inspectorId={orderStepsReturnAddNewLoadPlaceId}
                                    //
                                    onClick={onLoadPlaceAdd}
                                />
                            </div>
                        </>
                    ) : (
                        <div className={classNames(styles.initialViewRightSide)}>
                            <div className={classNames(styles.waybillTitle)}>{t('orderList')}</div>
                            <div className={classNames(styles.addLoadPlaceDescription)}>
                                {t('title')}
                                <br />
                                {t('subtitle')}
                            </div>
                            <div className={classNames(styles.addLoadPlaceButton)}>
                                <Button
                                    inspectorId={orderStepsReturnAddFirstLoadPlaceId}
                                    onClick={onLoadPlaceAdd}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className={classNames(styles.bottomRight)}>
                    <ProgressBarBoxes
                        arrowDirection={enArrowDirection.Left}
                        name={t('orderReturnProgressBarTitle')}
                        itemsCount={filledTrailerCapacity}
                        maxItemsCount={maxTrailerCapacity}
                        remainingTrailerSpace={remainingTrailerSpace}
                    />
                    {maxCapacityReached && (
                        <label className={styles.blockedButtonsLabel}>{t('blockedButtons')}</label>
                    )}
                    {carrierStackEnabled && (
                        <div className={styles.stackEnabledInfo}>
                            <StyledWarningIcon />
                            <label className={styles.stackEnabledInfoText}>
                                {t('carrierStackEnabled')}
                            </label>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default withInspector<OrderReturnProps>(OrderReturn, {});

export type OrderReturnProps = {
    data?: OrderReturnData;
} & OrderReturnDisplayOptions;

export class OrderReturnDisplayOptions {
    className?: string;
    //
    createReturnStepsCollectionDataSource?: (returnStepsCollectionId?: string) => DataSource; // elementy typu OrderForwardStepData
    createForwardStepsCollectionDataSource?: (
        forwardStepsCollectionId?: string
    ) => DataSource | DataSourceOptions | undefined;
    insertOrderReturnStep?: (
        payload: insertOrderReturnStepToCollectionPayload
    ) => Promise<OrderReturnStepItem>;
    //
    removeStep?: (payload: deleteItemPayload) => Promise<void>;
    validateOrder?: () => Promise<OrderValidationResult | undefined>;
    getRemainingTrailerSpace?: (
        payload: getRemainingReturnTrailerSpacePayload
    ) => Promise<RemainingTrailerSpace | undefined>;
}

export type OrderReturnStepItem = OrderReturnStepData;
export const OrderReturnStepItemKeys = OrderReturnStepDataKeys;

export type OrderReturnData = {
    id?: string;
    returnSteps?: {
        id?: string;
        //
        filledTrailerCapacity?: number;
        maxTrailerCapacity?: number;
        items?: {
            id?: IdType;
        };
    };
    forwardSteps?: {
        id?: string;
        items?: {
            id?: IdType;
        }[];
    };
};

export const OrderReturnDataKeys = [
    'id',
    'returnSteps.id',
    'returnSteps.filledTrailerCapacity',
    'returnSteps.maxTrailerCapacity',
    'forwardSteps.items.id',
    'returnSteps.items.id',
];
